import { groupsApi } from "@/services/api.groups.service";
import { AreaNode } from "@/types/groups/AreaNode";
import { Vue, Prop, Watch, Component } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { LOAD_CHILDREN_OPTIONS, ASYNC_SEARCH, LOAD_ROOT_OPTIONS } from "@riophae/vue-treeselect";
import TreeselectNode from "@/types/area-tree/TreeselectNode";
import { DeviceNode } from "@/types/devices/DeviceNode";
import { AreaTreeNodeType } from "@/types/area-tree/AreaTreeNodeType";
import { AreaContentConfiguration } from "@/types/area-tree/AreaContentConfiguration";
import { AreaTreeContent } from "@/types/area-tree/AreaTreeContent";
import { uniq } from "lodash";
import { toCommaSeperatedList } from "@/utils/arrayUtils";

const Areas = namespace("areas");
const Regions = namespace("regions");

/**
 * Internal node which represents a container for additional content, belonging to a group.
 */
interface ContentContainerNode extends TreeselectNode {
	nodeType: AreaTreeNodeType;
	contentNodeType: AreaTreeNodeType;
	parentGroupId: number;
	icon: string;
	class: string;
}

/**
 * Internal node which represents a single item of additional content (such as a camera, or a map layer), belonging
 * to a group.
 */
interface ContentNode extends TreeselectNode {
	nodeType: AreaTreeNodeType;
	parentGroupId: number;
	icon: string;
}

/**
 * Base class supporting area tree implementations that use the vue-treeselect component. Loads the area tree,
 * and provides default implementations of event handlers for the tree select.
 */
@Component
export default class AreaTreeBase extends Vue {
	$refs!: {
		treeSelect: any;
	};

	/**
	 * The value of the select, containing the currently selected node(s), after applying the `reduce` function. Along
	 * with the `input` event, provides support for `v-model`.
	 */
	@Prop() protected value: any;

	/**
	 * Defines the permissions a user must have for a group to be included in the tree. By default, any group where the
	 * user has some permission (Any) will be included.
	 */
	@Prop({ default: () => ["Any"] }) protected requiredPermissions: string[];

	/**
	 * Whether or not to display the clear button in the select.
	 */
	@Prop({ default: true }) protected clearable: boolean;

	/**
	 * If set to true, the user will be able to select multiple groups from the tree. The `input` event will be emitted
	 * with an array of nodes.
	 */
	@Prop({ default: false }) protected multiple: boolean;

	/**
	 * Whether or not the select should be read-only.
	 */
	@Prop({ default: false }) protected readonly: boolean;

	/**
	 * Intended for backwards compatability with the previous area select. If set to true, and the user is a system
	 * admin, they will be able to see all tenants (not just the ones they have access too).
	 */
	@Prop({ default: false }) protected multiTenant: boolean;

	/**
	 * The placeholder text to display in the select when the user has not selected a group.
	 */
	@Prop({ default: "Select an area" }) protected placeholder: string;

	/**
	 * If the system is setup to support regions. The `regionId` can be passed through to get the correct tree
	 * structure for the target region.
	 */
	@Prop({ default: null }) protected regionId: number | null;

	/**
	 * Configuration for devices to be displayed within the tree. See the
	 * [Displaying Content](https://dev.azure.com/sureviewcc/CC/_wiki/wikis/ImmixCC.wiki/920/Displaying-Content)
	 * page for more information.
	 */
	@Prop({ default: () => [] }) protected contentConfig: AreaContentConfiguration[];

	/**
	 * A set of additional items to include at the start the area tree (useful to insert a System Area, for example).
	 */
	@Prop({ default: () => [] }) protected itemsToPrepend: TreeselectNode[];

	/**
	 * If set to true, the first node in the tree will be automatically selected when it's finished loading.
	 */
	@Prop({ default: false }) protected autoSelectGroup: boolean;

	/**
	 * Reduce function to apply to any nodes selected by the user. For example, if you just want the select to give you
	 * group ID's (and not the whole node), you can use a reduce function such as `(node) => node.id`. Can also be used
	 * with devices, see the
	 * [Displaying Content](https://dev.azure.com/sureviewcc/CC/_wiki/wikis/ImmixCC.wiki/920/Displaying-Content) page.
	 */
	@Prop({ type: Function, default: option => option }) protected reduce: (areaNode: AreaNode | DeviceNode) => any;

	/**
	 * Filter function to apply to the areas displayed within the tree. If a filter is specified, each area node will
	 * be passed to the function; if it returns `true` the area will be displayed, `false` will cause it to be hidden.
	 */
	@Prop({ type: Function, default: null }) protected filter: (areaNode: AreaNode) => boolean;

	@Areas.State protected areaDictionary: { [groupId: number]: string };
	@Areas.Action protected fetchAreaDictionary: (regionUrl?: string | null) => Promise<void>;

	@Regions.Getter protected getRegionUrl: (regionId: number) => string;

	/**
	 * The currently loaded area nodes.
	 */
	protected areaTree: AreaNode[] = [];
	protected isLoadingAreas: boolean = false;
	protected isSearching: boolean = false;
	protected isSearchComplete: boolean = false;

	/**
	 * Value formatted to use within the treeselect.
	 */
	protected get valueFormat(): string {
		if (this.multiple) {
			return "id";
		}
		return typeof this.value === "number" ? "id" : "node";
	}

	/**
	 * Returns true if we're displaying additional content (cameras, map layers, etc) in the tree.
	 */
	protected get isDisplayingAdditionalContent(): boolean {
		return this.contentConfig && this.contentConfig.length > 0;
	}

	/**
	 * Builds a string containing the titles of the items being displayed in the tree. Defaults to "areas", but will
	 * include additional content titles configured with contentConfig.
	 */
	protected get treeContentTitles(): string {
		return toCommaSeperatedList([
			"areas",
			...this.contentConfig.map(content => content.contentTitle.toLowerCase())
		]);
	}

	/**
	 * Gets the current region URL, if regionId is set. Otherwise returns null.
	 */
	protected get regionUrl(): string {
		if (!this.regionId) {
			return null;
		}

		return this.getRegionUrl(this.regionId);
	}

	/**
	 * Returns the area tree, with additional options specified by the itemsToPrepend prop included.
	 */
	protected get areaTreeWithAdditions(): AreaNode[] {
		/**
		 * Note: When the options are re-computed (e.g. due to the filter), we manually initialize the tree on the
		 * next tick.
		 *
		 * If you have async search enabled and defaultOptions set to true, lazy-loading of child nodes in the tree
		 * (such as cameras) becomes extremely slow after 2-3 clicks. To workaround this, we set defaultOptions to
		 * point at areaTreeWithAdditions, and call initialize() ourselves whenever it changes (such as on first
		 * load, and whenever devices are loaded in). This allows us to retain async searching, but also avoid the
		 * slow-down when modifying the tree.
		 */

		this.$nextTick(() => {
			this.$refs.treeSelect.initialize();
		});

		var tree = [...(this.itemsToPrepend as AreaNode[]), ...this.areaTree];

		if (!this.filter) {
			return tree;
		} else {
			return this.filterTreeDepthFirst(tree, this.filter);
		}
	}

	/**
	 * Re-loads the area tree when the region ID is changed.
	 */
	@Watch("regionId")
	private async onRegionChanged(newRegionId: number, oldRegionId: number) {
		if (newRegionId != oldRegionId) {
			this.$emit("input", null);
			await this.setupTree(true);
		}
	}

	/**
	 * Sets up the area tree on mount.
	 */
	protected async mounted() {
		await this.setupTree();

		// Emit a component-loaded event in case we're within a generic modal
		this.$emit("component-loaded");
	}

	/**
	 * Cleans up the area tree.
	 */
	protected beforeDestroy() {
		this.areaTree = null;
	}

	/**
	 * Sets up the component. Fetches the area tree and dictionary from the data service, and loads the tree
	 * into the treeselect component.
	 * @param updateDictionary Whether the area dictionary should be re-populated, even if it's been already loaded.
	 */
	private async setupTree(updateDictionary: boolean = false) {
		// Note 1: In Ops, this component takes into account a 'tenant size' that is calculated in the DS, using the
		// total number of groups in the system.If we ever start to see slowdown in the UI, we'll need to bring
		// those changes across. Ideal strategies would be:
		// 	- Small Customers = Load full tree
		// 	- Medium Customers = Ideally, we'd lazy-load the root nodes, and the children, as the user
		// 		navigates the tree. This would need a new GetRootGroupTreeNodes method adding to the DS.
		//  - Large Customers = Only allow the user to search for groups.

		if (Object.keys(this.areaDictionary).length == 0 || updateDictionary) {
			// May need to make the area dictionary per-region, we'll see..
			await this.fetchAreaDictionary(this.regionUrl);
		}

		this.isLoadingAreas = true;
		this.areaTree = await this.fetchFullAreaTree();

		this.$nextTick(() => {
			this.isLoadingAreas = false;

			if (this.autoSelectGroup) {
				// If we're set to select an initial group, select the first node in the tree.
				const node = this.areaTreeWithAdditions[0];
				this.onNodeSelectedBase(node);
			}
		});
	}

	/**
	 * Handles when the value of the treeselect changes.
	 * @param node The area node.
	 */
	protected onInputChanged(node: AreaNode | DeviceNode) {
		if (!node) {
			this.$emit("input", null);
			return;
		}
	}

	/**
	 * Handles when the user selects a node in the treeselect.
	 * @param node The node the user has selected.
	 */
	protected onNodeSelectedBase(node: AreaNode | DeviceNode) {
		if (this.multiple) {
			let result = [];
			if (this.value) {
				result = [...this.value];
			}

			result.push(this.reduce(node));
			this.$emit("input", result);
		} else {
			this.$emit("input", this.reduce(node));
		}
	}

	/**
	 * Handles when the user de-selects a node in the treeselect.
	 * @param node The node the user has de-selected.
	 */
	protected onNodeDeselected(node: AreaNode | DeviceNode) {
		if (this.multiple) {
			let result = [...this.value];
			let nodeToRemove = result.findIndex(n => n === node.id);
			if (nodeToRemove !== -1) {
				result.splice(nodeToRemove, 1);
			}

			this.$emit("input", result);
		} else {
			this.$emit("input", this.reduce(node));
		}
	}

	/**
	 * Handles when the treeselect is closed by the user. Resets our
	 * search state.
	 */
	protected onClose() {
		this.isSearching = false;
	}

	/**
	 * Handles when the user begins searching and enters a query.
	 * @param searchQuery The search query.
	 */
	protected async onSearchValueChanged(searchQuery: string) {
		this.isSearching = searchQuery.length > 0;
	}

	/**
	 * Handler for when the treeselect needs to lazily load a branch of the tree, or for when the user performs an
	 * asynchronous search.
	 */
	private async loadOptions({ action, parentNode, callback, searchQuery }) {
		switch (action) {
			case LOAD_ROOT_OPTIONS: {
				// We shouldn't get here as root options should be loaded on mount.
				break;
			}
			case LOAD_CHILDREN_OPTIONS: {
				if (parentNode.children) {
					// Node already has children loaded, so we don't need to do anything.
					return;
				}

				switch (parentNode.nodeType) {
					case AreaTreeNodeType.Container: {
						await this.fetchContentIntoContainer(parentNode);
						break;
					}
					case AreaTreeNodeType.Group: {
						await this.fetchChildrenIntoGroup(parentNode);
						break;
					}
				}
				break;
			}
			case ASYNC_SEARCH: {
				await this.searchTree(searchQuery, callback);
				break;
			}
		}
	}

	/**
	 * Fetches the full area tree for the user.
	 * @returns The full area tree.
	 */
	protected async fetchFullAreaTree() {
		const newAreaTree = await groupsApi.getFullGroupsTree(
			this.requiredPermissions,
			this.multiTenant,
			this.regionUrl
		);

		// Traverse the tree, and format leaf nodes.
		this.traverseTreeDepthFirst(newAreaTree, node => {
			this.addUnloadedContentContainerNodes(node);
			this.formatLeafNodes(node);
		});

		return newAreaTree;
	}

	/**
	 * Fetches just the top 2 levels of the area tree. Useful for when the tree is being lazily loaded and the user
	 * expands nodes. This method adds device containers if they have been configured by the user.
	 * Note: Currently unused.
	 * @returns The root nodes of the area tree, and any immediate descendants.
	 */
	protected async fetchRootAreaTree() {
		const newAreaTree = await groupsApi.getRootGroupTree(
			this.requiredPermissions,
			this.multiTenant,
			this.regionUrl
		);

		this.traverseTreeDepthFirst(newAreaTree, node => {
			this.addUnloadedContentContainerNodes(node);
			this.formatLeafNodes(node);
		});

		return newAreaTree;
	}

	/**
	 * Loads the children for a specified group node into the tree.
	 * @param groupNode The parent group node.
	 */
	private async fetchChildrenIntoGroup(groupNode: AreaNode) {
		let { children } = await groupsApi.getGroupTreeNode(
			groupNode.id,
			this.requiredPermissions,
			this.multiTenant,
			this.regionUrl
		);

		groupNode.children = children;

		// TODO: Update this to include the filter when lazy loading of groups is required.
		this.traverseTreeDepthFirst([groupNode], node => {
			this.addUnloadedContentContainerNodes(node);
			this.formatLeafNodes(node);
		});

		// Re-initialize the tree.
		this.$refs.treeSelect.initialize();
	}

	/**
	 * Loads content into a specified container node, using the nodeType and associated config onLoad callback.
	 * @param containerNode The container node in the tree to load content into.
	 */
	private async fetchContentIntoContainer(containerNode: ContentContainerNode) {
		const { contentNodeType: nodeType, parentGroupId: groupId } = containerNode;

		// Find the associated content configuration.
		const contentConfig = this.contentConfig.find(content => content.nodeType == nodeType);

		// Invoke the onLoad handler to load the content for our parent group.
		const content = await contentConfig.onLoad(groupId, this.regionUrl);

		// Map the content nodes and append them to the container.
		containerNode.children = content.map(content => this.mapContentNode(content, contentConfig, groupId));

		// Re-initialize the tree.
		this.$refs.treeSelect.initialize();
	}

	/**
	 * Performs an async search for groups and content, and merges the two trees.
	 * @param query The search query.
	 * @param callback The callback to use when passing back the search results to the treeselect.
	 */
	private async searchTree(query: string, callback: (error: string | null, result: TreeselectNode[]) => void) {
		this.isSearching = true;
		this.isSearchComplete = false;

		let areaNodes: AreaNode[],
			contentNodes: AreaTreeContent[] = [];

		if (this.isDisplayingAdditionalContent) {
			const contentSearches = this.contentConfig.map(async config => {
				let searchResults = await config.onSearch(query, this.regionUrl);
				return searchResults.map(
					result =>
						({
							...result,
							nodeType: config.nodeType
						} as AreaTreeContent)
				);
			});

			contentNodes = (await Promise.all(contentSearches)).flatMap(s => s);
			const groupsToInclude = uniq(contentNodes.map(content => content.groupId));

			areaNodes = await groupsApi.searchGroupTree(
				query,
				this.requiredPermissions,
				this.multiTenant,
				this.regionUrl,
				groupsToInclude
			);
		} else {
			// Only searching groups.
			areaNodes = await groupsApi.searchGroupTree(
				query,
				this.requiredPermissions,
				this.multiTenant,
				this.regionUrl
			);
		}

		if (!areaNodes) {
			// No search results, return an empty array.
			callback(null, []);
		} else {
			// We got search results, so traverse the resulting group tree and add the devices to their group.
			this.traverseTreeDepthFirst(areaNodes, node => {
				this.addPopulatedContentContainerNodes(
					node,
					contentNodes.filter(content => content.groupId == node.id)
				);
				this.formatLeafNodes(node);
			});

			// Return our new tree (filtered, if the filter prop is set).
			callback(null, this.filter ? this.filterTreeDepthFirst(areaNodes, this.filter) : areaNodes);
		}

		this.isSearchComplete = true;
	}

	/**
	 * Normalizes leaf nodes in the tree. Leaf nodes returned from the data service will have children set to an empty
	 * array, whereas the treeselect expects leaf nodes to have their children set to undefined (or be omitted).
	 * @param treeNode The tree node to format.
	 */
	private formatLeafNodes(treeNode: TreeselectNode) {
		if (treeNode.children == null) {
			return;
		}

		if (treeNode.children.length == 0) {
			treeNode.children = undefined;
		}
	}

	/**
	 * Adds unloaded content container nodes to a tree node, one for each configured in contentConfig (if any).
	 * @param treeNode The tree node to add content container nodes too.
	 */
	private addUnloadedContentContainerNodes(treeNode: AreaNode) {
		if (!this.isDisplayingAdditionalContent || treeNode.children == null) {
			// Don't add the content nodes if we're not configured to display any, or the node's children are unloaded.
			return;
		}

		const contentContainerNodes =
			this.contentConfig?.map(c => this.buildContainerNode(c, treeNode.id as number)) ?? [];

		treeNode.children = [...contentContainerNodes, ...treeNode.children] as AreaNode[];
	}

	/**
	 * Adds content container nodes to a group node, one for each in contentConfig, and then appends a specified set of
	 * children to the container nodes.
	 * @param treeNode The tree node to add content container nodes too.
	 * @param contentForGroup The child contents to add under the container.
	 */
	private addPopulatedContentContainerNodes(treeNode: AreaNode, contentForGroup: AreaTreeContent[]) {
		if (!this.isDisplayingAdditionalContent) {
			return;
		}

		const contentContainerNodes = this.contentConfig
			.map(contentConfig => {
				const content = contentForGroup
					.filter(content => content.nodeType == contentConfig.nodeType)
					.map(content => this.mapContentNode(content, contentConfig, treeNode.id));

				if (content.length > 0) {
					return this.buildContainerNode(contentConfig, treeNode.id, content, true);
				} else {
					if (treeNode.groupTypeId === 1 && treeNode.children.length === 0 || treeNode.children === null) {
						// If the searchQuery matches an area name, show all contentConfig options collapsed
						return this.buildContainerNode(contentConfig, treeNode.id, null, false);
					}
					return null;
				}
			})
			.filter(node => !!node);

		treeNode.children = [...contentContainerNodes, ...treeNode.children] as AreaNode[];
	}

	/**
	 * Helper method. Builds a container node for a specified tree content config.
	 * @param deviceConfig The content configuration to build the container for.
	 * @param groupId The ID of the parent group.
	 * @param children The children (content) of this node, or null if they should be lazy-loaded.
	 * @param isSearchResult Whether or not this container node contains search results.
	 * @returns A container node for the specified tree content configuration.
	 */
	private buildContainerNode(
		contentConfig: AreaContentConfiguration,
		groupId: number,
		children: ContentNode[] = null,
		isSearchResult: boolean = false
	): ContentContainerNode {
		let containerId = {
			type: AreaTreeNodeType.Container,
			contentTitle: contentConfig.contentTitle,
			groupId
		};

		if (isSearchResult) {
			containerId["s"] = 1;
		}

		return {
			id: JSON.stringify(containerId),
			nodeType: AreaTreeNodeType.Container,
			contentNodeType: contentConfig.nodeType,
			parentGroupId: groupId,
			label: contentConfig.contentTitle,
			icon: contentConfig.icon,
			children: children,
			isDisabled: true,
			isDefaultExpanded: isSearchResult,
			class: "vue-treeselect__container-node"
		};
	}

	/**
	 * Helper method. Maps a content node returned from the onLoad/onSearch handlers into a node we can insert into
	 * the tree.
	 * @param content The content node returned by the handler.
	 * @param contentConfig The content configuration.
	 * @param groupId The id of the group this item of content belongs too.
	 * @returns A @see InternalContentNode for the specified content.
	 */
	private mapContentNode(
		content: AreaTreeContent,
		contentConfig: AreaContentConfiguration,
		groupId: number
	): ContentNode {
		const itemId = content[contentConfig.idField];

		if (itemId == undefined) {
			throw new Error("AreaTreeBase: Content item does not have an ID");
		}

		return {
			...content,
			id: JSON.stringify({
				type: contentConfig.nodeType,
				id: itemId
			}),
			nodeType: contentConfig.nodeType,
			parentGroupId: groupId,
			icon: contentConfig.icon
		};
	}

	/**
	 * Helper method which performs a filter on the tree nodes, and their descendants, depth first. If a group is
	 * included because it's children match (but it doesn't), it will be marked as disabled.
	 * @param nodes The parent nodes.
	 * @param filter The filter to apply to the nodes.
	 * @returns The set of filtered nodes.
	 */
	private filterTreeDepthFirst<T extends TreeselectNode & { children?: T[] }>(
		nodes: T[],
		filter: (treeNode: T) => boolean
	): T[] {
		if (!nodes || nodes.length == 0) {
			return nodes;
		}

		return nodes
			.map(node => {
				var nodeMatches = filter(node);
				var children = this.filterTreeDepthFirst<T>(node.children, filter) ?? [];

				var includeNode = nodeMatches || children.length > 0;

				if (includeNode) {
					return {
						...node,
						children: children.length > 0 ? children : undefined,
						isDisabled: !nodeMatches
					};
				} else {
					return null;
				}
			})
			.filter(node => !!node);
	}

	/**
	 * Helper method. Executes a callback function on a set of tree nodes, and their descendants, depth-first.
	 * @param nodes The parent nodes.
	 * @param callback The callback to execute on each node.
	 */
	private traverseTreeDepthFirst<T extends TreeselectNode>(nodes: T[], callback: (treeNode: T) => void) {
		if (!nodes || nodes.length == 0) {
			return;
		}

		nodes.forEach(node => {
			this.traverseTreeDepthFirst(node.children, callback);
			callback(node);
		});
	}
}
